import React from 'react';
import { 
  Box, 
  Heading, 
  Text, 
  Flex, 
  Tab, 
  Tabs, 
  TabList, 
  TabPanel, 
  TabPanels,
  Code,
  Divider,
  Button,
  useColorModeValue
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import AleaDownloadButton from '../../components/AleaDownloadButton/AleaDownloadButton';
import PageHeader from '../../components/PageHeader/PageHeader';

const DownloadButtonGuide: React.FC = () => {
  const navigate = useNavigate();
  const bgColor = useColorModeValue('gray.50', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  // Example download options
  const basicOptions = [
    {
      id: 'pdf',
      label: 'Export as PDF',
      onClick: () => console.log('PDF Export clicked')
    },
    {
      id: 'print',
      label: 'Print',
      onClick: () => console.log('Print clicked')
    }
  ];

  const extendedOptions = [
    {
      id: 'pdf',
      label: 'Export as PDF',
      onClick: () => console.log('PDF Export clicked')
    },
    {
      id: 'csv',
      label: 'Export as CSV',
      onClick: () => console.log('CSV Export clicked')
    },
    {
      id: 'excel',
      label: 'Export as Excel',
      onClick: () => console.log('Excel Export clicked')
    },
    {
      id: 'print',
      label: 'Print',
      onClick: () => console.log('Print clicked')
    }
  ];

  const disabledOption = [
    {
      id: 'pdf',
      label: 'Export as PDF',
      onClick: () => console.log('PDF Export clicked')
    },
    {
      id: 'csv',
      label: 'Export as CSV (Disabled)',
      onClick: () => console.log('CSV Export clicked'),
      disabled: true
    }
  ];

  return (
    <Flex flexFlow="column">
      <PageHeader title={"Download Button"} />
      <Box p={8} bg="#fff" minH="100vh" borderRadius="20px">
        <Box maxW="1200px" mx="auto">
          <Flex mb={8} align="center">
            <Button
              leftIcon={<ChevronLeftIcon />}
              variant="ghost"
              onClick={() => navigate('/style-guide')}
              mr={4}
            >
              Back to Style Guide
            </Button>
          </Flex>

          <Text mb={6}>
            AleaDownloadButton is a reusable component that provides a dropdown menu with download options.
            It's designed to be flexible and can be configured with different options based on the context.
          </Text>

          <Tabs variant="enclosed" mb={8}>
            <TabList>
              <Tab>Examples</Tab>
              <Tab>Props</Tab>
              <Tab>Usage</Tab>
            </TabList>

            <TabPanels>
              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Basic Usage</Heading>
                  <Text mb={4}>
                    Basic button with two options: PDF export and Print. Click to see dropdown options.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4} display="flex" justifyContent="center">
                    <AleaDownloadButton options={basicOptions} />
                  </Box>
                </Box>

                <Divider my={6} />

                <Box mb={8}>
                  <Heading size="md" mb={4}>Extended Options</Heading>
                  <Text mb={4}>
                    Component with multiple download options (PDF, CSV, Excel, Print).
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4} display="flex" justifyContent="center">
                    <AleaDownloadButton options={extendedOptions} />
                  </Box>
                </Box>

                <Divider my={6} />

                <Box mb={8}>
                  <Heading size="md" mb={4}>With Disabled Option</Heading>
                  <Text mb={4}>
                    Example showing disabled options. The CSV option (second) is disabled.
                  </Text>
                  <Box bg={bgColor} p={6} borderRadius="md" borderWidth="1px" borderColor={borderColor} mb={4} display="flex" justifyContent="center">
                    <AleaDownloadButton options={disabledOption} />
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Props</Heading>
                  <Box overflowX="auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Prop</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Type</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Default</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>options</Code></td>
                          <td style={{ padding: '12px' }}><Code>DownloadOption[]</Code></td>
                          <td style={{ padding: '12px' }}><Code>[]</Code></td>
                          <td style={{ padding: '12px' }}>Array of download options to display in the dropdown</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>iconSize</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}><Code>"22"</Code></td>
                          <td style={{ padding: '12px' }}>Size of the download icon</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>buttonSize</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}><Code>"50px"</Code></td>
                          <td style={{ padding: '12px' }}>Size of the button (width and height)</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>buttonBg</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}><Code>"white"</Code></td>
                          <td style={{ padding: '12px' }}>Background color of the button</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>buttonHoverBg</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}><Code>"#f8f8f8"</Code></td>
                          <td style={{ padding: '12px' }}>Background color of the button on hover</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>dropdownWidth</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}><Code>"196px"</Code></td>
                          <td style={{ padding: '12px' }}>Width of the dropdown menu</td>
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>

                <Box mb={8}>
                  <Heading size="md" mb={4}>DownloadOption Interface</Heading>
                  <Box overflowX="auto">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Property</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Type</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Required</th>
                          <th style={{ textAlign: 'left', padding: '12px', fontWeight: 600 }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>id</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}>Yes</td>
                          <td style={{ padding: '12px' }}>Unique identifier for the option</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>label</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}>Yes</td>
                          <td style={{ padding: '12px' }}>Display text for the option</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>translationKey</Code></td>
                          <td style={{ padding: '12px' }}><Code>string</Code></td>
                          <td style={{ padding: '12px' }}>No</td>
                          <td style={{ padding: '12px' }}>Translation key for i18n (uses label if not provided)</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>onClick</Code></td>
                          <td style={{ padding: '12px' }}><Code>{'() => void'}</Code></td>
                          <td style={{ padding: '12px' }}>Yes</td>
                          <td style={{ padding: '12px' }}>Function to execute when option is clicked</td>
                        </tr>
                        <tr style={{ borderBottom: '1px solid #E2E8F0' }}>
                          <td style={{ padding: '12px' }}><Code>disabled</Code></td>
                          <td style={{ padding: '12px' }}><Code>boolean</Code></td>
                          <td style={{ padding: '12px' }}>No</td>
                          <td style={{ padding: '12px' }}>Whether the option is disabled</td>
                        </tr>
                      </tbody>
                    </table>
                  </Box>
                </Box>
              </TabPanel>

              <TabPanel>
                <Box mb={8}>
                  <Heading size="md" mb={4}>Usage</Heading>
                  <Code p={4} borderRadius="md" w="100%" display="block" whiteSpace="pre">
  {`// Step 1: Import the component
  import AleaDownloadButton from '../../components/AleaDownloadButton/AleaDownloadButton';

  // Step 2: Create handler functions
  const handleExportPdf = () => {
    // PDF export logic
    console.log('Exporting as PDF');
  };

  const handlePrint = () => {
    // Print logic
    window.print();
  };

  // Step 3: Define options
  const downloadOptions = [
    {
      id: 'pdf',
      label: 'Export as PDF',
      onClick: handleExportPdf
    },
    {
      id: 'print',
      label: 'Print',
      onClick: handlePrint
    }
  ];

  // Step 4: Use in JSX
  <AleaDownloadButton options={downloadOptions} />`}
                  </Code>
                </Box>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </Box>            
    </Flex>
  );
};

export default DownloadButtonGuide; 